import React, { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSelect from 'components/MDSelect';

import { numOr0 } from 'sectors/pilates/members/components/paymentReceive/ui';

const METHOD_OPTS = [
  { value: 'CASH', label: 'Nakit' },
  { value: 'CARD', label: 'Kart' },
  { value: 'TRANSFER', label: 'Havale/EFT' },
  { value: 'OTHER', label: 'Diğer' },
];

function ymdToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

function safeStr(x) {
  return (x ?? '').toString();
}

export function PaymentEditModal({
  open,
  payment,
  memberLabel = 'Üye',
  onClose,
  onSave, // async (paymentId, patch) => void
}) {
  const theme = useMuiTheme();

  const [amountTry, setAmountTry] = useState('');
  const [paidDate, setPaidDate] = useState('');
  const [method, setMethod] = useState('CASH');
  const [note, setNote] = useState('');

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const paymentId = payment?.id;

  // init form
  useEffect(() => {
    if (!open) return;

    setErr('');
    setSaving(false);

    setAmountTry(payment?.amountTry != null ? String(payment.amountTry) : '');
    setPaidDate(payment?.paidDate || ymdToday());
    setMethod(payment?.method || 'CASH');
    setNote(safeStr(payment?.note));
  }, [open, payment]);

  const amount = useMemo(() => numOr0(amountTry), [amountTry]);

  const canSave = !!paymentId && amount > 0 && !!paidDate && !saving;

  const handleSave = async () => {
    if (!canSave) return;

    try {
      setSaving(true);
      setErr('');

      // PATCH DTO (backend ile netleştiririz)
      const patch = {
        amountTry: amount,
        paidDate,
        method,
        note: note?.trim() || null,
      };

      await onSave?.(paymentId, patch);
      onClose?.();
    } catch (e) {
      setErr(e?.message || 'Ödeme güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          background: `linear-gradient(180deg, ${alpha(theme.palette.info.main, 0.1)}, ${alpha(
            theme.palette.background.paper,
            0.95
          )})`,
        }}
      >
        <MDBox
          sx={{
            width: 36,
            height: 36,
            borderRadius: '12px',
            display: 'grid',
            placeItems: 'center',
            border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
            background: alpha(theme.palette.info.main, 0.08),
          }}
        >
          <Icon sx={{ fontSize: 18, color: alpha(theme.palette.info.main, 0.95) }}>edit</Icon>
        </MDBox>

        <MDBox>
          <MDTypography fontWeight="bold">Ödemeyi Düzenle</MDTypography>
          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
            {memberLabel} • #{paymentId ?? '—'}
          </MDTypography>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <MDBox sx={{ display: 'grid', gap: 1.25 }}>
          <MDBox>
            <MDTypography fontWeight="bold">Tutar (₺)</MDTypography>
            <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
              Ödeme tutarını güncelle
            </MDTypography>
          </MDBox>

          <MDInput
            value={amountTry}
            onChange={(e) => setAmountTry(e.target.value)}
            placeholder="0"
            fullWidth
            inputProps={{ inputMode: 'decimal' }}
            disabled={saving}
          />

          <MDBox
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}
          >
            <MDBox>
              <MDTypography fontWeight="bold">Ödeme Tarihi</MDTypography>
              <MDInput
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                fullWidth
                disabled={saving}
              />
            </MDBox>

            <MDBox>
              <MDTypography fontWeight="bold">Ödeme Yöntemi</MDTypography>
              <MDSelect
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                fullWidth
                disabled={saving}
              >
                {METHOD_OPTS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </MDSelect>
            </MDBox>
          </MDBox>

          <MDBox>
            <MDTypography fontWeight="bold">Not</MDTypography>
            <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
              Örn: Borç tahsilatı / Erken ödeme / Mat satışı
            </MDTypography>
          </MDBox>

          <MDInput
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Not yaz…"
            fullWidth
            disabled={saving}
          />

          {err ? (
            <MDBox
              sx={{
                p: 1,
                borderRadius: '12px',
                border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
                background: alpha(theme.palette.error.main, 0.08),
              }}
            >
              <MDTypography variant="caption" sx={{ color: 'error.main' }}>
                {err}
              </MDTypography>
            </MDBox>
          ) : null}
        </MDBox>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          disabled={saving}
          sx={{ borderRadius: '12px' }}
        >
          Vazgeç
        </MDButton>
        <MDBox sx={{ flex: 1 }} />
        <MDButton
          variant="contained"
          color="info"
          onClick={handleSave}
          disabled={!canSave}
          sx={{ borderRadius: '12px' }}
        >
          {saving ? 'Kaydediliyor...' : 'Kaydet'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
